import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { AlertTriangle, Shield, Activity, Users, Cpu, Database, Lock, Briefcase, Monitor, FileText, Presentation, Search, Code, Brain, Link, Scale, Globe } from 'lucide-react';
import CapsuleConsole from "./CapsuleConsole";
import PersonaTierPanel from "./PersonaTierPanel";
import TrustChainAuditFeed from "./TrustChainAuditFeed";
import TrustChainMapUI from "./TrustChainMapUI";
import SalesCommandBriefing from "./SalesCommandBriefing";
import DemoEnvToggle from "./DemoEnvToggle";
import PatentProtectionPanel from "./PatentProtectionPanel";
import BriefingDeckGenerator from "./BriefingDeckGenerator";
import OSINTThreatDetectionPanel from "./OSINTThreatDetectionPanel";
import CapsuleDevKitPanel from "./CapsuleDevKitPanel";
import KnowledgePulsePanel from "./KnowledgePulsePanel";
import PersonaTetherPanel from "./PersonaTetherPanel";
import GovernanceSecurityPanel from "./GovernanceSecurityPanel";
import ChatUI from './ChatUI';
import EvidenceExplorer from './EvidenceExplorer';
import ClawEcosystemPanel from './ClawEcosystemPanel';
import AuditExportButton from './AuditExportButton';
import FleetStatusBar from './FleetStatusBar';
import KillSwitchButton from './KillSwitchButton';
// AskAgentomy moved to App.tsx for global availability
import LiveIndicator from './LiveIndicator';
import { darkOpsTheme } from '../themes/DarkOpsTheme';
import { fetchHealth, fetchMetrics } from '../lib/api';
import { isProduction, safeFallback } from '../lib/production-guard';
import { isPanelVisible, getModeLabel } from '../config/ui-modes';
import { useUIMode } from '../hooks/useUIMode';
import { useDemoMode } from './DemoContext';
import { useSSE } from '../hooks/useSSE';
import { SAMPLE_DATA_BADGE, sampleDataDisclaimer } from '../lib/disclaimers.generated';

// -- Animated counter hook: counts from 0 to target over ~1 second
function useAnimatedCounter(target: number, duration = 1000): number {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setDisplay(target);
      return;
    }

    let startTime: number | null = null;

    const step = (ts: number) => {
      if (!startTime) startTime = ts;
      const elapsed = ts - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out quad
      const eased = 1 - (1 - progress) * (1 - progress);
      setDisplay(Math.round(target * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return display;
}

// -- Staggered reveal wrapper
function StaggerReveal({ children, index }: { children: React.ReactNode; index: number }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) { setVisible(true); return; }
    const timer = setTimeout(() => setVisible(true), index * 100);
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <div
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 0.4s ease, transform 0.4s ease',
      }}
    >
      {children}
    </div>
  );
}

// -- Animated metric value display
function AnimatedMetricValue({ value, suffix, className }: { value: number; suffix?: string; className?: string }) {
  const animated = useAnimatedCounter(value);
  return <span className={className}>{animated}{suffix || ''}</span>;
}

function useSafeDemoMode() {
  try {
    return useDemoMode();
  } catch {
    return { isDemoMode: false, activeScenario: null, scenarios: [], setIsDemoMode: () => {}, setActiveScenario: () => {} };
  }
}

class ErrorBoundaryFallback extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  override render() {
    if (this.state.hasError) {
      return <div className="p-8 text-center text-[#A8A29E]"><p className="text-lg mb-2">This panel encountered an error.</p><button className="text-cyan-400 underline" onClick={() => this.setState({ hasError: false })}>Try again</button></div>;
    }
    return this.props.children;
  }
}

interface SystemMetrics {
  activeCapsules: number;
  totalCapsules: number;
  overrideEvents24h: number;
  agentLoadIndex: number;
  trustChainAlerts: number;
  memoryUsage: number;
  cpuUtilization: number;
  activeConnections: number;
  uptime: number;
  heapUsedMB: number;
  heapTotalMB: number;
  rssMB: number;
  securityEnabled: boolean;
  securityTiers: string[];
  dbConnected: boolean;
  cacheConnected: boolean;
  coreModules: number;
  trustAnchors: number;
  auditLogSize: number;
  lastUpdate: string;
}

const DEMO_METRICS: SystemMetrics = {
  activeCapsules: 41,
  totalCapsules: 43,
  overrideEvents24h: 5,
  agentLoadIndex: 68,
  trustChainAlerts: 1,
  memoryUsage: 58,
  cpuUtilization: 37,
  activeConnections: 19,
  uptime: 172800,
  heapUsedMB: 48,
  heapTotalMB: 128,
  rssMB: 156,
  securityEnabled: true,
  securityTiers: ['Analyst', 'Builder', 'Operator', 'Strategist'],
  dbConnected: true,
  cacheConnected: true,
  coreModules: 43,
  trustAnchors: 4,
  auditLogSize: 512,
  lastUpdate: new Date().toISOString(),
};

export default function EnterpriseDashboard() {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(safeFallback({ ...DEMO_METRICS }));
  const [, setIsConnected] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [isUsingDemoData, setIsUsingDemoData] = useState(true);
  const { mode: uiMode, setMode: setUIMode, isInternal, activateInternal } = useUIMode();
  const { isDemoMode: globalDemoMode, activeScenario } = useSafeDemoMode();

  // SSE real-time event stream - primary data source, polling is fallback
  const { connected: sseStatus, lastUpdated: sseLastUpdated } = useSSE('/api/events/stream', {
    onEvent: (event) => {
      // Update metrics on incoming SSE events when not in demo mode
      const demoActive = isProduction() ? false : isDemoMode;
      if (!demoActive && event.type) {
        setMetrics(prev => prev ? { ...prev, lastUpdate: event.timestamp || new Date().toISOString() } : prev);
        setIsConnected(true);
      }
    },
  });

  const [showKeyPrompt, setShowKeyPrompt] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [keyError, setKeyError] = useState(false);
  const [showKeyText, setShowKeyText] = useState(false);
  const [rememberKey, setRememberKey] = useState(() => {
    try { return localStorage.getItem('agentomy-remember-internal') === 'true'; } catch { return false; }
  });

  // Ctrl+Shift+I to toggle internal mode activation prompt
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        if (isInternal) {
          // Already internal, toggle back to public
          setUIMode('public');
        } else {
          setShowKeyPrompt(true);
          setKeyInput('');
          setKeyError(false);
        }
      }
      if (e.key === 'Escape' && showKeyPrompt) {
        setShowKeyPrompt(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isInternal, setUIMode, showKeyPrompt]);

  const handleKeySubmit = useCallback(() => {
    const success = activateInternal(keyInput);
    if (success) {
      setShowKeyPrompt(false);
      setKeyInput('');
      setKeyError(false);
      if (rememberKey) {
        try { localStorage.setItem('agentomy-remember-internal', 'true'); } catch {}
      }
    } else {
      setKeyError(true);
    }
  }, [keyInput, activateInternal, rememberKey]);

  // Helper: should this panel be shown in the current UI mode?
  const showPanel = useCallback((panelId: string) => isPanelVisible(panelId, uiMode), [uiMode]);

  // Apply global demo scenario metrics when demo mode is active
  useEffect(() => {
    if (globalDemoMode && activeScenario) {
      setMetrics(prev => prev ? {
        ...prev,
        activeCapsules: activeScenario.metrics.modules,
        totalCapsules: activeScenario.metrics.modules + 2,
        overrideEvents24h: activeScenario.metrics.alerts,
        agentLoadIndex: activeScenario.metrics.efficiency,
        trustChainAlerts: activeScenario.metrics.alerts,
        activeConnections: activeScenario.metrics.agents,
        lastUpdate: new Date().toISOString(),
      } : prev);
    }
  }, [globalDemoMode, activeScenario]);

  // In production, force demo mode off
  const effectiveDemoMode = isProduction() ? false : isDemoMode;

  useEffect(() => {
    if (effectiveDemoMode) {
      // Demo mode: simulate real-time data with random values (dev only)
      const updateMetrics = () => {
        setMetrics(prev => prev ? {
          ...prev,
          agentLoadIndex: Math.floor(Math.random() * 100),
          cpuUtilization: Math.floor(Math.random() * 80),
          memoryUsage: Math.floor(Math.random() * 90),
          lastUpdate: new Date().toISOString()
        } : prev);
        setIsConnected(true);
      };

      const interval = setInterval(updateMetrics, 5000);
      return () => clearInterval(interval);
    }

    // Live mode: poll real API endpoints
    const pollData = async () => {
      try {
        const [health, metricsData] = await Promise.all([
          fetchHealth(),
          fetchMetrics(),
        ]);

        if (!health || !metricsData) { setIsConnected(false); return; }
        const mem = metricsData.metrics?.memoryUsage || {};
        const cpu = metricsData.metrics?.cpuUsage || {};
        const bbx = metricsData.metrics?.blackBoxMetrics || {};

        setMetrics({
          activeCapsules: health.capsules?.healthy || 0,
          totalCapsules: health.capsules?.total || 0,
          overrideEvents24h: bbx.auditLogSize || 0,
          agentLoadIndex: Math.round(((health.capsules?.healthy || 0) / (health.capsules?.total || 1)) * 100),
          trustChainAlerts: health.capsules?.unhealthy || 0,
          memoryUsage: mem.heapTotal ? Math.round((mem.heapUsed / mem.heapTotal) * 100) : 0,
          cpuUtilization: cpu.user ? Math.round(cpu.user / 1000000) : 0,
          activeConnections: metricsData.metrics?.totalAgents || 0,
          uptime: Math.round(health.uptime || 0),
          heapUsedMB: Math.round((mem.heapUsed || 0) / 1048576),
          heapTotalMB: Math.round((mem.heapTotal || 0) / 1048576),
          rssMB: Math.round((mem.rss || 0) / 1048576),
          securityEnabled: health.security?.enabled || health.securityStatus?.enabled || false,
          securityTiers: health.security?.tiers || health.securityStatus?.tiers || [],
          dbConnected: health.services?.database || false,
          cacheConnected: health.services?.cache || false,
          coreModules: bbx.capsules || 35,
          trustAnchors: bbx.trustAnchors || 0,
          auditLogSize: bbx.auditLogSize || 0,
          lastUpdate: new Date().toISOString(),
        });
        setIsConnected(true);
        setIsUsingDemoData(false);
      } catch {
        setIsConnected(false);
      }
    };

    pollData();
    const interval = setInterval(pollData, 15000);
    return () => clearInterval(interval);
  }, [effectiveDemoMode]);

  const getStatusColor = (value: number, threshold: number = 80) => {
    if (value >= threshold) return 'text-ag-critical';
    if (value >= threshold * 0.7) return 'text-ag-warning';
    return 'text-ag-active';
  };

  return (
    <div
      className="min-h-screen p-6"
      style={{
        backgroundColor: darkOpsTheme.colors.background,
        color: darkOpsTheme.colors.foreground,
        fontFamily: darkOpsTheme.fonts.body,
        position: 'relative',
      }}
    >
      {/* Animated gradient line at top */}
      <div
        className="dashboard-gradient-line"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent, #06b6d4, #8b5cf6, #06b6d4, transparent)',
          backgroundSize: '200% 100%',
          animation: 'gradientFlow 8s linear infinite',
        }}
      />
      <style>{`
        @keyframes gradientFlow {
          0% { background-position: 0% 0%; }
          100% { background-position: 200% 0%; }
        }
        @keyframes tabPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @media (prefers-reduced-motion: reduce) {
          .dashboard-gradient-line { animation: none !important; }
          .tab-pulse-dot { animation: none !important; }
        }
      `}</style>

      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2">
            Agentomy Command Center
            <span className="text-sm font-normal opacity-40 ml-3">v0.31.0</span>
          </h1>
          <div className="flex items-center space-x-4 text-sm opacity-70">
            <LiveIndicator
              color={sseStatus === 'connected' ? 'green' : sseStatus === 'polling' ? 'amber' : 'red'}
              label={sseStatus === 'connected' ? 'Live' : sseStatus === 'polling' ? 'Polling (15s)' : 'Disconnected'}
            />
            <span>Last Update: {sseLastUpdated ? sseLastUpdated.toLocaleTimeString() : (metrics ? new Date(metrics.lastUpdate).toLocaleTimeString() : 'N/A')}</span>
            <div className="flex items-center space-x-1">
              <div className={`w-2 h-2 rounded-full ${sseStatus === 'connected' ? 'bg-ag-active' : sseStatus === 'polling' ? 'bg-ag-warning' : 'bg-ag-critical'}`}></div>
              <span>{sseStatus === 'connected' ? 'Connected' : sseStatus === 'polling' ? 'Polling' : 'Disconnected'}</span>
            </div>
          </div>
        </div>

        <div className="flex space-x-2 items-center">
          {!isProduction() && (
            <button
              onClick={() => setIsDemoMode(!isDemoMode)}
              className={`px-3 py-1 rounded text-xs font-bold cursor-pointer transition-colors ${
                isDemoMode
                  ? 'bg-orange-600 text-white hover:bg-orange-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isDemoMode ? 'DEMO -- Click for LIVE' : 'LIVE -- Click for DEMO'}
            </button>
          )}
          {/* UI Mode Badge */}
          <Badge
            variant="outline"
            className={isInternal
              ? "text-purple-400 border-purple-400 cursor-pointer"
              : "text-[#A8A29E] border-[#5A534B] cursor-pointer"
            }
            onClick={() => {
              if (isInternal) {
                setUIMode(uiMode === 'internal' ? 'public' : 'internal');
              } else {
                setShowKeyPrompt(true);
                setKeyInput('');
                setKeyError(false);
              }
            }}
          >
            {getModeLabel(uiMode)}
          </Badge>
          <Badge variant="outline" className="text-cyan-400 border-cyan-400">
            Security Level: ALPHA
          </Badge>
          <Badge variant="outline" className="text-ag-active border-ag-active">
            System: OPERATIONAL
          </Badge>
        </div>
      </div>

      {/* Internal Key Activation Prompt */}
      {showKeyPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-900 border border-[#3A352F] rounded-lg p-6 w-96 shadow-xl">
            <h3 className="text-lg font-semibold text-white mb-2">Activate Internal Mode</h3>
            <p className="text-[#A8A29E] text-sm mb-4">Enter the team access key to unlock all panels.</p>
            <div className="relative mb-3">
              <input
                type={showKeyText ? "text" : "password"}
                value={keyInput}
                onChange={(e) => { setKeyInput(e.target.value); setKeyError(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleKeySubmit(); }}
                placeholder="Access key"
                autoFocus
                autoComplete="off"
                spellCheck={false}
                className={`w-full px-3 py-2 pr-10 bg-[#292524] border rounded text-white text-sm focus:outline-none focus:ring-1 ${
                  keyError ? 'border-red-500 focus:ring-red-500' : 'border-[#4A443D] focus:ring-cyan-500'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowKeyText(!showKeyText)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#A8A29E] text-xs"
                tabIndex={-1}
              >
                {showKeyText ? 'Hide' : 'Show'}
              </button>
            </div>
            {keyError && <p className="text-red-400 text-xs mb-3">Invalid key. Try again.</p>}
            <label className="flex items-center space-x-2 mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberKey}
                onChange={(e) => {
                  setRememberKey(e.target.checked);
                  if (!e.target.checked) {
                    try { localStorage.removeItem('agentomy-remember-internal'); } catch {}
                  }
                }}
                className="rounded border-[#4A443D] bg-[#292524] text-cyan-500 focus:ring-cyan-500"
              />
              <span className="text-[#A8A29E] text-sm">Remember on this device</span>
            </label>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowKeyPrompt(false)}
                className="px-4 py-2 text-sm text-[#A8A29E] hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleKeySubmit}
                className="px-4 py-2 text-sm bg-cyan-600 text-white rounded hover:bg-cyan-700 transition-colors"
              >
                Activate
              </button>
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="metrics" className="space-y-6">
        <TabsList className="bg-gray-900 border-[#3A352F] flex-wrap h-auto gap-1 p-2">
          {showPanel('metrics') && (
            <TabsTrigger value="metrics" className="data-[state=active]:bg-[#C2714F]">
              <Activity className="w-4 h-4 mr-2" />
              System Metrics{isUsingDemoData && <span className="text-xs font-normal text-amber-400 ml-1" title={sampleDataDisclaimer("dashboard")}>({SAMPLE_DATA_BADGE})</span>}
              <span
                className="tab-pulse-dot"
                style={{
                  display: 'inline-block',
                  width: '5px',
                  height: '5px',
                  borderRadius: '50%',
                  backgroundColor: '#34D399',
                  marginLeft: '6px',
                  animation: 'tabPulse 3s ease-in-out infinite',
                }}
              />
            </TabsTrigger>
          )}
          {showPanel('chat') && (
            <TabsTrigger value="chat" className="data-[state=active]:bg-[#C2714F]">
              Chat
            </TabsTrigger>
          )}
          {showPanel('knowledge-pulse') && (
            <TabsTrigger value="knowledge-pulse" className="data-[state=active]:bg-[#C2714F]">
              <Brain className="w-4 h-4 mr-2" />
              Knowledge Pulse
            </TabsTrigger>
          )}
          {showPanel('modules') && (
            <TabsTrigger value="modules" className="data-[state=active]:bg-[#C2714F]">
              <Shield className="w-4 h-4 mr-2" />
              Module Console
            </TabsTrigger>
          )}
          {showPanel('personas') && (
            <TabsTrigger value="personas" className="data-[state=active]:bg-[#C2714F]">
              <Users className="w-4 h-4 mr-2" />
              Security Access
            </TabsTrigger>
          )}
          {showPanel('trust') && (
            <TabsTrigger value="trust" className="data-[state=active]:bg-[#C2714F]">
              <Lock className="w-4 h-4 mr-2" />
              Audit Trail
            </TabsTrigger>
          )}
          {showPanel('trustmap') && (
            <TabsTrigger value="trustmap" className="data-[state=active]:bg-[#C2714F]">
              <Shield className="w-4 h-4 mr-2" />
              Verification Map
            </TabsTrigger>
          )}
          {showPanel('sales') && (
            <TabsTrigger value="sales" className="data-[state=active]:bg-[#C2714F]">
              <Briefcase className="w-4 h-4 mr-2" />
              Sales Briefing
            </TabsTrigger>
          )}
          {showPanel('demo') && (
            <TabsTrigger value="demo" className="data-[state=active]:bg-[#C2714F]">
              <Monitor className="w-4 h-4 mr-2" />
              Demo Control
            </TabsTrigger>
          )}
          {showPanel('evidence') && (
            <TabsTrigger value="evidence" className="data-[state=active]:bg-[#C2714F]">
              <Search className="w-4 h-4 mr-2" />
              Live Proof
            </TabsTrigger>
          )}
          {showPanel('claw-ecosystem') && (
            <TabsTrigger value="claw-ecosystem" className="data-[state=active]:bg-[#C2714F]">
              <Globe className="w-4 h-4 mr-2" />
              Claw Ecosystem
            </TabsTrigger>
          )}
          {showPanel('patents') && (
            <TabsTrigger value="patents" className="data-[state=active]:bg-[#C2714F]">
              <FileText className="w-4 h-4 mr-2" />
              Patent Protection
            </TabsTrigger>
          )}
          {showPanel('briefing') && (
            <TabsTrigger value="briefing" className="data-[state=active]:bg-[#C2714F]">
              <Presentation className="w-4 h-4 mr-2" />
              Briefing Decks
            </TabsTrigger>
          )}
          {showPanel('osint') && (
            <TabsTrigger value="osint" className="data-[state=active]:bg-[#C2714F]">
              <Search className="w-4 h-4 mr-2" />
              OSINT Threats
            </TabsTrigger>
          )}
          {showPanel('dev-kit') && (
            <TabsTrigger value="dev-kit" className="data-[state=active]:bg-[#C2714F]">
              <Code className="w-4 h-4 mr-2" />
              Dev Kit
            </TabsTrigger>
          )}
          {showPanel('tethers') && (
            <TabsTrigger value="tethers" className="data-[state=active]:bg-[#C2714F]">
              <Link className="w-4 h-4 mr-2" />
              Tethers
            </TabsTrigger>
          )}
          {showPanel('governance') && (
            <TabsTrigger value="governance" className="data-[state=active]:bg-[#C2714F]">
              <Scale className="w-4 h-4 mr-2" />
              Governance
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="metrics" className="space-y-6">
          {globalDemoMode && (
            <p className="text-xs text-gray-500 italic">Real-time fleet health metrics. In production, these update every 5 seconds from the governance API.</p>
          )}
          {!metrics ? (
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-6 text-center text-ag-critical">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                <p>Unable to load metrics. API connection required.</p>
              </CardContent>
            </Card>
          ) : (<>
          {/* Primary Metrics Grid */}
          <div className="grid grid-cols-4 gap-6">
            <StaggerReveal index={0}>
              <Card className="bg-gray-900 border-[#3A352F] hover:border-cyan-500 transition-colors">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-cyan-400" />
                    Modules Active
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-cyan-400"><AnimatedMetricValue value={metrics.activeCapsules} /></div>
                  <div className="text-sm text-[#A8A29E] mt-1">of {metrics.totalCapsules} total</div>
                </CardContent>
              </Card>
            </StaggerReveal>

            <StaggerReveal index={1}>
              <Card className="bg-gray-900 border-[#3A352F] hover:border-ag-warning transition-colors">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2 text-ag-warning" />
                    Override Events
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-ag-warning"><AnimatedMetricValue value={metrics.overrideEvents24h} /></div>
                  <div className="text-sm text-[#A8A29E] mt-1">last 24 hours</div>
                </CardContent>
              </Card>
            </StaggerReveal>

            <StaggerReveal index={2}>
              <Card className="bg-gray-900 border-[#3A352F] hover:border-blue-500 transition-colors">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center">
                    <Cpu className="w-5 h-5 mr-2 text-blue-400" />
                    Agent Load Index
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-4xl font-bold ${getStatusColor(metrics.agentLoadIndex)}`}>
                    <AnimatedMetricValue value={metrics.agentLoadIndex} suffix="%" />
                  </div>
                  <div className="text-sm text-[#A8A29E] mt-1">cluster utilization</div>
                </CardContent>
              </Card>
            </StaggerReveal>

            <StaggerReveal index={3}>
              <Card className="bg-gray-900 border-[#3A352F] hover:border-ag-critical transition-colors">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center">
                    <Database className="w-5 h-5 mr-2 text-ag-critical" />
                    Audit Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-ag-critical"><AnimatedMetricValue value={metrics.trustChainAlerts} /></div>
                  <div className="text-sm text-[#A8A29E] mt-1">requiring attention</div>
                </CardContent>
              </Card>
            </StaggerReveal>
          </div>

          {/* Secondary Metrics */}
          <div className="grid grid-cols-3 gap-6">
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle>System Resources</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span>Memory Usage</span>
                    <span className={getStatusColor(metrics.memoryUsage)}>{metrics.memoryUsage}%</span>
                  </div>
                  <div className="w-full bg-[#3A352F] rounded-full h-2">
                    <div 
                      className="bg-[#D4825F] h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${metrics.memoryUsage}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span>CPU Utilization</span>
                    <span className={getStatusColor(metrics.cpuUtilization)}>{metrics.cpuUtilization}%</span>
                  </div>
                  <div className="w-full bg-[#3A352F] rounded-full h-2">
                    <div 
                      className="bg-blue-400 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${metrics.cpuUtilization}%` }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle>Runtime Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Uptime</span>
                    <span className="text-green-400">{Math.floor(metrics.uptime / 3600)}h {Math.floor((metrics.uptime % 3600) / 60)}m</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Heap Used</span>
                    <span className="text-cyan-400">{metrics.heapUsedMB} / {metrics.heapTotalMB} MB</span>
                  </div>
                  <div className="flex justify-between">
                    <span>RSS Memory</span>
                    <span className="text-yellow-400">{metrics.rssMB} MB</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Active Agents</span>
                    <span className="text-ag-active">{metrics.activeConnections}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle>Security Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Security</span>
                    <span className={metrics.securityEnabled ? "text-ag-active" : "text-ag-critical"}>{metrics.securityEnabled ? 'ACTIVE' : 'DISABLED'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Access Tiers</span>
                    <span className="text-cyan-400">{metrics.securityTiers.length} active</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Core Modules</span>
                    <span className="text-green-400">{metrics.coreModules}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Trust Anchors</span>
                    <span className="text-cyan-400">{metrics.trustAnchors}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Database</span>
                    <span className={metrics.dbConnected ? "text-ag-active" : "text-ag-warning"}>{metrics.dbConnected ? 'Connected' : 'Offline'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cache</span>
                    <span className={metrics.cacheConnected ? "text-ag-active" : "text-ag-warning"}>{metrics.cacheConnected ? 'Connected' : 'Offline'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          </>)}
        </TabsContent>

        {showPanel('chat') && (
          <TabsContent value="chat" className="mt-4">
            <ChatUI isDemoMode={effectiveDemoMode} />
          </TabsContent>
        )}

        {showPanel('knowledge-pulse') && (
          <TabsContent value="knowledge-pulse">
            <KnowledgePulsePanel />
          </TabsContent>
        )}

        {showPanel('modules') && (
          <TabsContent value="modules">
            <CapsuleConsole />
          </TabsContent>
        )}

        {showPanel('personas') && (
          <TabsContent value="personas">
            <PersonaTierPanel />
          </TabsContent>
        )}

        {showPanel('trust') && (
          <TabsContent value="trust">
            <div className="space-y-4">
              <div className="flex justify-end">
                <AuditExportButton />
              </div>
              <TrustChainAuditFeed />
            </div>
          </TabsContent>
        )}

        {showPanel('trustmap') && (
          <TabsContent value="trustmap">
            <TrustChainMapUI />
          </TabsContent>
        )}

        {showPanel('sales') && (
          <TabsContent value="sales">
            <SalesCommandBriefing />
          </TabsContent>
        )}

        {showPanel('demo') && (
          <TabsContent value="demo">
            <DemoEnvToggle onToggle={setIsDemoMode} />
          </TabsContent>
        )}

        {showPanel('evidence') && (
          <TabsContent value="evidence">
            <EvidenceExplorer />
          </TabsContent>
        )}

        {showPanel('claw-ecosystem') && (
          <TabsContent value="claw-ecosystem">
            <ClawEcosystemPanel />
          </TabsContent>
        )}

        {showPanel('patents') && (
          <TabsContent value="patents">
            <PatentProtectionPanel />
          </TabsContent>
        )}

        {showPanel('briefing') && (
          <TabsContent value="briefing">
            <React.Suspense fallback={<div className="p-8 text-[#A8A29E]">Loading briefing deck...</div>}>
              <ErrorBoundaryFallback>
                <BriefingDeckGenerator />
              </ErrorBoundaryFallback>
            </React.Suspense>
          </TabsContent>
        )}

        {showPanel('osint') && (
          <TabsContent value="osint">
            <OSINTThreatDetectionPanel />
          </TabsContent>
        )}

        {showPanel('dev-kit') && (
          <TabsContent value="dev-kit">
            <CapsuleDevKitPanel />
          </TabsContent>
        )}

        {showPanel('tethers') && (
          <TabsContent value="tethers">
            <PersonaTetherPanel />
          </TabsContent>
        )}

        {showPanel('governance') && (
          <TabsContent value="governance">
            <div className="space-y-4">
              <FleetStatusBar />
              <div className="flex justify-end">
                <KillSwitchButton />
              </div>
              <GovernanceSecurityPanel />
            </div>
          </TabsContent>
        )}
      </Tabs>

    </div>
  );
}